import * as React from 'react';
import * as moment from 'moment';
import { IndividualReportBlock, PVAPage, PVAUserSelect, PVASelect, PVAActionsButtonGroup } from '../../../components';
import { UserPermissions, Style } from '../../../models';
import { hasPermission } from '../../../utilities/User';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import {ReportDataService, StyleDataService, UserDataService, CharacteristicGroupDataService, SurveyReportTypeDataService, SurveyDataService} from '../../../services';
import { ReportType, MidRangeType } from '../../../enums';
import { BaseRouteProps } from '../../../interfaces';
import { exportToPDF } from '../../../utilities/Pdf';
import { PdfFoundations } from './pdf_foundations';
import { exportDetailedPages } from './pdf_detailed';

interface IndividualReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

interface IndividualReportState {
    loading: boolean;
    error: boolean;
    reportType: ReportType;
    midRangeType: MidRangeType;
    small: boolean;
    showNotes: boolean;
    pdfLoader: boolean;
    allStyles: Style[];
    individualReportUserId?: number;
    individualReportUserData?: any;
    fetchedOrgId?: number;
    fetchedSurveyId?: number;
    fetchedSurveyVersionId?: number;
    hasFoundations?: boolean;
    characteristicGroups: { [id: number]: any };
    characteristics: { [id: number]: any };
    surveyReportTypes: any[];
}

@inject('UserStore', 'FilterStore') @observer
export class IndividualReport extends React.Component<IndividualReportProps, IndividualReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private userDataService = new UserDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private surveyReportTypeDataService = new SurveyReportTypeDataService(this.props.notificationService);
    private surveyDataService = new SurveyDataService(this.props.notificationService);

    constructor(props: IndividualReportProps) {
        super(props);

        this.state = {
            loading: false,
            error: false,
            reportType: ReportType.Standard,
            midRangeType: MidRangeType.Default,
            small: false,
            showNotes: false,
            pdfLoader: false,
            allStyles: [],
            individualReportUserId: undefined,
            individualReportUserData: undefined,
            fetchedOrgId: 0,
            fetchedSurveyId: 0,
            fetchedSurveyVersionId: 0,
            characteristicGroups: {},
            characteristics: {},
            surveyReportTypes: []
        };
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users;
        }
        return [];
    }

    @computed get selectedUserData() {
        return this.state.individualReportUserData;
    }

    public userChange(e: any) {
        const userId: number = Number(e.target.value);

        if (this.props.FilterStore!.survey && userId) {
            this.setState({ loading: true, error: false, individualReportUserId: userId, individualReportUserData: undefined });
            let surveyVersionID: number | undefined = undefined;
            if (this.props.FilterStore!.surveyVersion && !this.props.FilterStore!.surveyVersion!.published) {
                surveyVersionID = this.props.FilterStore!.surveyVersion!.id;
            }

            const currentSurveyId = this.props.FilterStore!.survey!.id;
            const currentSurveyVersionId = surveyVersionID || 0;
            const currentOrgId = this.props.FilterStore!.organization ? this.props.FilterStore!.organization.id : 0;

            this.reportDataService.getIndividualReportData(this.props.FilterStore!.survey!.id, userId, surveyVersionID).then(async (reportData: any) => {
                const reportUserId = reportData.userId || reportData.userID;
                let hasFoundations = false;

                // Check Foundations permission BEFORE setting loading to false
                if (reportUserId) {
                    try {
                        const user = await this.userDataService.get(reportUserId);
                        hasFoundations = hasPermission(user.userPermissions, UserPermissions.Foundations);
                    } catch (error) {
                        console.error('Error checking user permissions:', error);
                    }
                }

                this.setState({
                    individualReportUserData: reportData,
                    loading: false,
                    fetchedSurveyId: currentSurveyId,
                    fetchedSurveyVersionId: currentSurveyVersionId,
                    fetchedOrgId: currentOrgId,
                    hasFoundations: hasFoundations,
                });

                const resolvedSurveyVersionId = reportData.surveyVersionId || reportData.surveyVersionID || (this.props.FilterStore!.surveyVersion ? this.props.FilterStore!.surveyVersion!.id : 0); if (resolvedSurveyVersionId) {
                    this.loadSurveyReportTypes(resolvedSurveyVersionId);
                }
                this.loadCharacteristicGroups(reportData);

                if (reportData.allStyles) {
                    this.setState({ allStyles: reportData.allStyles });
                } else if (resolvedSurveyVersionId) {
                    this.styleDataService.getAllBySurveyVersionId(resolvedSurveyVersionId).then((styles: Style[]) => {
                        this.setState({ allStyles: styles });
                    });
                }
            }).catch(() => {
                // Handle error if needed, but at least don't crash
                this.setState({ loading: false, error: true });
            });
        } else {
            this.setState({ loading: false, individualReportUserId: userId, individualReportUserData: undefined, hasFoundations: false });
        }
    }

    public instanceChange(e: any) {
        const instanceId: number = Number(e.target.value);
        if (instanceId) {
            this.setState({ loading: true, error: false, individualReportUserData: undefined });
            this.reportDataService.getIndividualReportDataByInstance(instanceId).then(async (reportData: any) => {
                let fetchedSurveyVersionId = 0;
                if (this.props.FilterStore!.surveyVersion && !this.props.FilterStore!.surveyVersion!.published) {
                    fetchedSurveyVersionId = this.props.FilterStore!.surveyVersion!.id;
                }
                const fetchedSurveyId = this.props.FilterStore!.survey ? this.props.FilterStore!.survey!.id : 0;

                // Check Foundations permission BEFORE setting loading to false
                const reportUserId = reportData.userId || reportData.userID;
                let hasFoundations = false;
                if (reportUserId) {
                    try {
                        const user = await this.userDataService.get(reportUserId);
                        hasFoundations = hasPermission(user.userPermissions, UserPermissions.Foundations);
                    } catch (error) {
                        console.error('Error checking user permissions:', error);
                    }
                }

                this.setState({
                    individualReportUserData: reportData,
                    loading: false,
                    individualReportUserId: reportData.userId || reportData.userID,
                    fetchedSurveyId: fetchedSurveyId,
                    fetchedSurveyVersionId: fetchedSurveyVersionId,
                    fetchedOrgId: this.props.FilterStore!.organization ? this.props.FilterStore!.organization.id : 0,
                    hasFoundations: hasFoundations
                });

                const resolvedSurveyVersionId = reportData.surveyVersionId || reportData.surveyVersionID || (this.props.FilterStore!.surveyVersion ? this.props.FilterStore!.surveyVersion!.id : 0); if (resolvedSurveyVersionId) {
                    this.loadSurveyReportTypes(resolvedSurveyVersionId);
                }
                this.loadCharacteristicGroups(reportData);

                if (reportData.allStyles) {
                    this.setState({ allStyles: reportData.allStyles });
                } else if (resolvedSurveyVersionId) {
                    this.styleDataService.getAllBySurveyVersionId(resolvedSurveyVersionId).then((styles: Style[]) => {
                        this.setState({ allStyles: styles });
                    });
                }
            }).catch(() => {
                this.setState({ loading: false, error: true });
            });
        }
    }

    private loadSurveyReportTypes(surveyVersionId: number) {
        this.surveyReportTypeDataService.getBySurveyVersion(surveyVersionId).then((surveyReportTypes: any[]) => {
            this.setState({ surveyReportTypes: surveyReportTypes });
        });
    }

    private loadCharacteristicGroups(reportData: any) {
        if (reportData && reportData.inclinationGroups) {
            reportData.inclinationGroups.forEach((inclinationGroup: any) => {
                const id = inclinationGroup.characteristicGroupID;
                if (id && !this.state.characteristicGroups[id]) {
                    this.characteristicGroupDataService.get(id).then((characteristicGroup: any) => {
                        this.setState((prevState) => {
                            const characteristics = { ...prevState.characteristics };
                            if (characteristicGroup.characteristicGroupCharacteristics && characteristicGroup.characteristicGroupCharacteristics.length > 0) {
                                characteristicGroup.characteristicGroupCharacteristics.forEach((c: any) => {
                                    if (c.characteristic) {
                                        characteristics[c.characteristic.id] = c.characteristic;
                                    }
                                });
                            }
                            return {
                                characteristicGroups: {
                                    ...prevState.characteristicGroups,
                                    [id]: characteristicGroup
                                },
                                characteristics: characteristics
                            };
                        });
                    });
                }
            });
        }
    }

    private HasAccessToMidrange() {
        return this.HasAccessToViewMidranges() && this.props.UserStore!.checkUserPermission(UserPermissions.CanViewMidRangeChoices);
    }
    private HasAccessToViewMidranges() {
        return (this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewMidRanges));
    }

    @computed get PdfExportEnabled() {
        return this.state.hasFoundations || !!this.state.individualReportUserId;
    }

    private exportPDF = () => {
        if (this.PdfExportEnabled) {
            // Using API-side PDF generation for now
            this.setState({ pdfLoader: true });
            this.reportDataService.getIndividualReportDataPDF(this.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then(() => {
                this.setState({ pdfLoader: false });
            }).catch((error: any) => {
                console.log(error);
                this.setState({ pdfLoader: false });
            });
        }
    }

    // Client-side PDF generation (preserved for future use)
    // To enable: Update exportPDF() to call this.exportDetailedPDF()
    private exportDetailedPDF() {
        if (!this.selectedUserData) return;

        this.setState({ pdfLoader: true }, async () => {
            try {
                const surveyVersion = (this.props.FilterStore && this.props.FilterStore.surveyVersion) || this.selectedUserData.surveyVersion;
                const survey = await this.surveyDataService.get(this.props.FilterStore!.survey!.id);
                const surveyTexts = survey.surveyTexts || [];
                const aboutSurveyText = ((surveyTexts as any[]).find((st: any) => st.surveyTextType === "individualReport") || {}).value || '';
                const organizationName = this.props.FilterStore && this.props.FilterStore.organization ? this.props.FilterStore.organization.name : '';

                const pages = exportDetailedPages({
                    selectedUserData: this.selectedUserData,
                    aboutSurveyText: aboutSurveyText,
                    surveyVersion: surveyVersion,
                    allStyles: this.state.allStyles,
                    notificationService: this.props.notificationService,
                    organizationName: organizationName,
                    surveyReportType: this.surveyReportType,
                    characteristicGroups: this.state.characteristicGroups,
                    characteristics: this.state.characteristics,
                    surveyReportTypes: this.state.surveyReportTypes
                });

                await exportToPDF(
                    pages,
                    `PVA_Individual_Report_${this.selectedUserData.name}.pdf`,
                    { html2canvas: { scale: 2, useCORS: true, allowTaint: true } }
                );
                this.setState({ pdfLoader: false });
            } catch (error) {
                console.log(error);
                this.setState({ pdfLoader: false });
            }
        });
    }

    private exportBriefPDF = () => {
        if (this.PdfExportEnabled) {
            this.setState({ pdfLoader: true });
            this.reportDataService.getIndividualBriefPDF(this.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then(() => {
                this.setState({ pdfLoader: false });
            }).catch((error: any) => {
                console.log(error);
                this.setState({ pdfLoader: false });
            });
        }
    }

    private getCurrentStyle() {
        if (this.selectedUserData && this.selectedUserData.style) {
            let userStyle = this.selectedUserData.style;
            if (this.state.allStyles.length > 0) {
                userStyle = this.state.allStyles.find((style: Style) => {
                    return style.name === this.selectedUserData.style.name;
                });
            }
            return userStyle;
        }
        return null;
    }

    private exportFoundationsPDF() {
        if (!this.selectedUserData) return;

        this.setState({ pdfLoader: true }, () => {
            const surveyVersion = (this.props.FilterStore && this.props.FilterStore.surveyVersion) || this.selectedUserData.surveyVersion;

            exportToPDF(
                <PdfFoundations
                    {...this.props}
                    selectedUserData={this.selectedUserData}
                    allStyles={this.state.allStyles}
                    surveyVersion={surveyVersion}
                    characteristicGroups={this.state.characteristicGroups}
                    characteristics={this.state.characteristics}
                    surveyReportTypes={this.state.surveyReportTypes}
                    notificationService={this.props.notificationService}
                />,
                `PVA_Foundations_${this.selectedUserData.name}.pdf`
            );
            this.setState({ pdfLoader: false });
        });
    }

    private exportNewUserGuidePDF() {
        this.setState({ pdfLoader: true }, () => {
            this.reportDataService.getNewUserGuidePDF("PVA Style Report User’s Guide").then(() => {
                this.setState({ pdfLoader: false });
            }).catch((error: any) => {
                console.log(error);
                this.setState({ pdfLoader: false });
            });
        });
    }

    private retryLoad = () => {
        if (this.selectedUserData && this.selectedUserData.surveyInstanceId) {
            this.instanceChange({ target: { value: this.selectedUserData.surveyInstanceId } });
        } else if (this.state.individualReportUserId) {
            this.userChange({ target: { value: this.state.individualReportUserId } });
        }
    }

    private update() {
        if (this.state.loading) return;
        if (this.props.FilterStore) {
            const currentOrgId = this.props.FilterStore.organization ? this.props.FilterStore.organization.id : 0;
            const currentSurveyId = this.props.FilterStore.survey ? this.props.FilterStore.survey.id : 0;
            let currentSurveyVersionId = 0;
            if (this.props.FilterStore.surveyVersion && !this.props.FilterStore.surveyVersion.published) {
                currentSurveyVersionId = this.props.FilterStore.surveyVersion.id;
            }

            if (this.state.fetchedOrgId !== currentOrgId ||
                this.state.fetchedSurveyId !== currentSurveyId ||
                this.state.fetchedSurveyVersionId !== currentSurveyVersionId) {

                const userStillValid = this.users.some(u => u.id === this.state.individualReportUserId);

                if (this.state.individualReportUserId && userStillValid && currentOrgId === this.state.fetchedOrgId) {
                    this.userChange({ target: { value: this.state.individualReportUserId } });
                } else {
                    this.setState({
                        loading: false,
                        individualReportUserId: undefined,
                        individualReportUserData: undefined,
                        fetchedOrgId: currentOrgId,
                        fetchedSurveyId: currentSurveyId,
                        fetchedSurveyVersionId: currentSurveyVersionId,
                        allStyles: [],
                        hasFoundations: false
                    });
                }
            }
        }
    }

    componentDidMount() {
        // No longer resetting FilterStore variables here as we are using local state
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public render() {
        const defaultOptions = [{
            id: 0,
            value: '- Clear Selected -'
        }];

        const selector = <PVAUserSelect defaultOptions={defaultOptions} options={this.users} selected={this.state.individualReportUserId} onChange={(e: any) => this.userChange(e)} />;

        const actionGroups: PVAActionsButtonGroup[] = [];

        if (this.state.reportType === ReportType.Midrange && this.HasAccessToMidrange()) {
            const items = [
                {
                    id: 'midrange-pva',
                    name: 'Mid Range: PVA',
                    callback: () => this.setState({ midRangeType: MidRangeType.Default })
                },
                {
                    id: 'midrange-team',
                    name: 'Mid Range: Current Team',
                    callback: () => this.setState({ midRangeType: MidRangeType.Team })
                },
                {
                    id: 'midrange-organization',
                    name: 'Mid Range: Current Organization',
                    callback: () => this.setState({ midRangeType: MidRangeType.Organization })
                }
            ];
            if (this.props.UserStore!.userRoleIn([Roles.SuperAdmin])) {
                items.push({
                    id: 'midrange-all',
                    name: 'Mid Range: All PVA Data',
                    callback: () => this.setState({ midRangeType: MidRangeType.All })
                });
            }
            actionGroups.push({ header: 'Mid Range Options', items });
        }

        if (this.HasAccessToViewMidranges()) {
            const items = [];
            if (this.state.reportType !== ReportType.Standard || this.state.showNotes) {
                items.push({
                    id: 'standard-view',
                    name: 'Standard View',
                    icon: 'standardIcon.svg', // icon mapping might be needed
                    description: 'View standard individual report',
                    callback: () => this.setState({ reportType: ReportType.Standard, showNotes: false })
                });
            }
            if (this.state.reportType !== ReportType.Midrange || this.state.showNotes) {
                items.push({
                    id: 'midrange-view',
                    name: 'Mid Range View',
                    icon: 'midrangeIcon.svg',
                    description: 'View the individual report with your scores as they relate to the mid-range of 1.25 standard deviation below and above the mean for each scale',
                    callback: () => this.setState({ reportType: ReportType.Midrange, showNotes: false })
                });
            }
            actionGroups.push({ header: 'Change View', items });
        }

        const sizeItems = [];
        // if (!this.state.hasFoundations) {
        //     if (!this.state.small) {
        //         sizeItems.push({
        //             id: 'view-small',
        //             name: 'View Small Report',
        //             icon: 'Icon awesome-search.svg',
        //             description: 'Show the report as a smaller version to better fit small screens',
        //             callback: () => this.setState({ small: true })
        //         });
        //     } else {
        //         sizeItems.push({
        //             id: 'view-normal',
        //             name: 'View Normal Report',
        //             icon: 'Icon awesome-search.svg',
        //             description: 'Show the report at its normal size',
        //             callback: () => this.setState({ small: false })
        //         });
        //     }
        //     actionGroups.push({ header: 'Report Size', items: sizeItems });
        // }

        const exportItems = [];
        if (this.PdfExportEnabled) {
            if (this.state.hasFoundations) {
                exportItems.push({
                    id: 'my-pva-style-report',
                    name: 'PVA Style Report',
                    icon: 'Icon awesome-file-pdf.svg',
                    description: 'My PVA Style Report in PDF Format',
                    callback: () => this.exportFoundationsPDF()
                });
                exportItems.push({
                    id: 'pva-style-report-user-guide',
                    name: "PVA Style Report User's Guide",
                    icon: 'Icon awesome-file-pdf.svg',
                    description: "PVA Style Report User's Guide in PDF Format",
                    callback: () => this.exportNewUserGuidePDF()
                });
            } else {
                exportItems.push({
                    id: 'export-brief-pdf',
                    name: 'Brief Report',
                    icon: 'Icon awesome-file-pdf.svg',
                    description: 'PDF Export',
                    callback: () => this.exportBriefPDF()
                });
                exportItems.push({
                    id: 'export-full-pdf',
                    name: 'Full-Detail Report',
                    icon: 'Icon awesome-file-pdf.svg',
                    description: 'PDF Export',
                    callback: () => this.exportPDF()
                });
            }
        }
        if (exportItems.length > 0) {
            actionGroups.push({ header: 'Export Options', items: exportItems });
        }

        if (this.selectedUserData && this.selectedUserData.others && this.selectedUserData.others.length > 0) {
            const items = this.selectedUserData.others.map((otherData: any) => {
                return {
                    id: `other-${otherData.id}`,
                    name: `${moment(otherData.dateTime).format('MM/DD/YYYY')} (${otherData.id})`,
                    icon: 'glyphicon-save-file',
                    dateTime: otherData.dateTime,
                    callback: () => this.instanceChange({ target: { value: otherData.id } })
                };
            }).sort((a: any, b: any) => {
                if (moment(a.dateTime).isBefore(b.dateTime)) {
                    return 1;
                } else if (moment(a.dateTime).isAfter(b.dateTime)) {
                    return -1;
                }
                return 0;
            });
            actionGroups.push({ header: 'Past Surveys', items });
        }

        if ((this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAssessmentNotes)) && !this.state.showNotes) {
            actionGroups.push({
                header: 'Other',
                items: [{
                    id: 'notes',
                    name: 'Notes',
                    icon: 'glyphicon-edit',
                    callback: () => this.setState({ showNotes: true })
                }]
            });
        }

        return <PVAPage>
            <PVAPage.Header actions={actionGroups}>
                <PVAPage.Header.Title>
                    <PVAPage.Header.Title.Text>
                        <span className="greyDark">PVA Individual Report<span className="trademark">TM</span> for: </span> 
                        <div>{selector}</div>
                    </PVAPage.Header.Title.Text>
                </PVAPage.Header.Title>
            </PVAPage.Header>
            <PVAPage.Body>
                {this.state.error && (
                    <div className="alert alert-danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Error retrieving individual report.</span>
                        <button className="btn btn-primary btn-sm" onClick={this.retryLoad}>Retry</button>
                    </div>
                )}
                {!this.state.error && (
                    <IndividualReportBlock 
                        loading={this.state.loading} 
                        userId={this.state.individualReportUserId || undefined} 
                        history={this.props.history} 
                        match={this.props.match} 
                        location={this.props.location} 
                        notificationService={this.props.notificationService} 
                        UserStore={this.props.UserStore} 
                        reportType={this.state.reportType}
                        midRangeType={this.state.midRangeType}
                        small={this.state.small}
                        showNotes={this.state.showNotes}
                        onNotesChange={(show) => this.setState({ showNotes: show })}
                        pdfLoader={this.state.pdfLoader}
                        survey={this.props.FilterStore!.survey}
                        surveyVersion={this.props.FilterStore!.surveyVersion}
                        noSurveys={this.props.FilterStore!.noSurveys}
                        organization={this.props.FilterStore!.organization}
                        surveyReportType={this.props.FilterStore!.surveyReportType}
                        users={this.props.FilterStore!.users}
                        teamId={this.props.FilterStore!.teamId}
                        selectedUserData={this.state.individualReportUserData}
                        showFoundations={this.state.hasFoundations}
                    />
                )}
            </PVAPage.Body>
            {!this.state.loading && <PVAPage.Footer />}
        </PVAPage>;
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/individual/IndividualReport.tsx